/**
 * Google Drive REST API v3 client.
 * Uses fetch() directly -- no SDK dependency.
 */

export interface DriveFileMetadata {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  description?: string;
}

export type DriveApiError =
  | { kind: 'token_expired' }
  | { kind: 'no_access' }
  | { kind: 'not_found' }
  | { kind: 'unknown'; status: number; message: string };

export type DriveApiResult =
  | { ok: true; data: DriveFileMetadata }
  | { ok: false; error: DriveApiError };

const FIELDS = 'id,name,mimeType,modifiedTime,description';

export async function fetchDriveMetadata(
  docId: string,
  accessToken: string,
): Promise<DriveApiResult> {
  const url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(docId)}?fields=${FIELDS}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    return { ok: false, error: { kind: 'unknown', status: 0, message: 'Network error' } };
  }

  if (response.ok) {
    const data: DriveFileMetadata = await response.json();
    return { ok: true, data };
  }

  switch (response.status) {
    case 401:
      return { ok: false, error: { kind: 'token_expired' } };
    case 403:
      return { ok: false, error: { kind: 'no_access' } };
    case 404:
      return { ok: false, error: { kind: 'not_found' } };
    default: {
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        message = body?.error?.message ?? message;
      } catch {
        // ignore parse failure
      }
      return { ok: false, error: { kind: 'unknown', status: response.status, message } };
    }
  }
}
